import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface CohortData {
  week: string;
  newUsers: number;
  retained: number;
  retentionRate: number;
}


interface RetentionCohortChartProps {
  data: CohortData[];
  title?: string;
  description?: string;
}

export function RetentionCohortChart({
  data,
  title = "Weekly Retention Cohorts",
  description = "New user retention by signup week"
}: RetentionCohortChartProps) {
  // Get color based on retention rate
  const getColor = (rate: number) => {
    if (rate >= 70) return '#10b981'; // green
    if (rate >= 50) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="week"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              label={{ value: 'Retention %', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number, _name: string, props) => {
                const payload = props.payload as CohortData;
                const { newUsers, retained } = payload;
                return [
                  <>
                    <div className="font-semibold">{value}% retained</div>
                    <div className="text-xs text-muted-foreground">
                      {retained} of {newUsers} users
                    </div>
                  </>,
                  'Retention Rate'
                ];
              }}
            />
            <Bar dataKey="retentionRate" radius={[8, 8, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.retentionRate)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }} />
            <span>Good (&gt;70%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }} />
            <span>Fair (50-70%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }} />
            <span>Poor (&lt;50%)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
